import * as PapaParse from 'papaparse';
import { CSVParseResult, RecipientLeadSheetRecord } from '../../types/organizer';

// Only DocumentType is required as a header
const REQUIRED_HEADER = 'DocumentType';

// Standard headers and their mapping to RecipientLeadSheetRecord properties
const STANDARD_HEADER_MAP: Record<string, 'uniqueId' | 'firstName' | 'lastName' | 'dateOfBirth'> = {
  UniqueID: 'uniqueId',
  FirstName: 'firstName',
  LastName: 'lastName',
  DateOfBirth: 'dateOfBirth',
  DOB: 'dateOfBirth', // alias
};

/**
 * Parses CSV text to RecipientLeadSheetRecord objects.
 * Only DocumentType header is required; all other headers are optional.
 * Any unrecognized header (excluding DocumentType) is treated as a wildcard field.
 */
export const parseCSVToRecipientRecords = (csvText: string): CSVParseResult => {
  if (!csvText || csvText.trim().length === 0) {
    throw new Error('File is empty');
  }

  const parsed = PapaParse.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
  });

  // Reject the entire file on any parse error (including non-fatal row-level issues)
  // to prevent partial uploads with silently dropped rows
  if (parsed.errors && parsed.errors.length > 0) {
    throw new Error(`Failed to parse CSV file: ${parsed.errors[0].message}`);
  }

  const headers: string[] = parsed.meta.fields || [];

  if (!headers.includes(REQUIRED_HEADER)) {
    throw new Error(`CSV is missing required header: ${REQUIRED_HEADER}`);
  }

  if (!parsed.data || parsed.data.length === 0) {
    throw new Error('CSV contains no data rows');
  }

  // Determine which standard fields are present
  const presentStandardFields: Array<keyof RecipientLeadSheetRecord> = [];
  const dobHeader = headers.includes('DateOfBirth') ? 'DateOfBirth' : headers.includes('DOB') ? 'DOB' : null;
  if (headers.includes('UniqueID')) presentStandardFields.push('uniqueId');
  if (headers.includes('FirstName')) presentStandardFields.push('firstName');
  if (headers.includes('LastName')) presentStandardFields.push('lastName');
  if (dobHeader) presentStandardFields.push('dateOfBirth');

  // Wildcard columns: anything not in STANDARD_HEADER_MAP and not DocumentType
  const knownHeaders = new Set([...Object.keys(STANDARD_HEADER_MAP), 'DocumentType']);
  const wildcardHeaders = headers.filter((h) => !knownHeaders.has(h));

  // mappableColumns: standard field keys + wildcard column names (no DocumentType)
  const mappableColumns: string[] = [...presentStandardFields, ...wildcardHeaders];

  const records: RecipientLeadSheetRecord[] = (parsed.data as any[]).map((row, index) => {
    const wildcardFields: Record<string, string> = {};
    for (const wh of wildcardHeaders) {
      wildcardFields[wh] = row[wh] ? String(row[wh]).trim() : '';
    }

    return {
      uniqueId: row['UniqueID'] ? String(row['UniqueID']).trim() : '',
      firstName: row['FirstName'] ? String(row['FirstName']).trim() : '',
      lastName: row['LastName'] ? String(row['LastName']).trim() : '',
      dateOfBirth: dobHeader && row[dobHeader] ? String(row[dobHeader]).trim() : '',
      documentType: row['DocumentType'] ? String(row['DocumentType']).trim() : '',
      wildcardFields,
      selectedForPrint: true,
      rowNumber: index + 2, // rowNumber reflects the physical CSV line number including the header (line 1)
    };
  });

  return { records, totalRows: records.length, mappableColumns };
};
